const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const escapeHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// Browsers use the document title as the suggested PDF filename.
export const archiveDocumentName = (label) => {
  const date = new Date().toISOString().slice(0, 10);
  const slug = String(label || 'document')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return `PVS-${slug || 'document'}-${date}`;
};

const collectCssText = async () => {
  const parts = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      parts.push(Array.from(sheet.cssRules).map((rule) => rule.cssText).join('\n'));
    } catch {
      if (sheet.href) {
        try {
          const response = await fetch(sheet.href);
          if (response.ok) parts.push(await response.text());
        } catch {
          // Ignore stylesheets that cannot be inlined.
        }
      }
    }
  }
  return parts.join('\n');
};

const inlineImages = async (clone, original) => {
  const cloneImgs = Array.from(clone.querySelectorAll('img'));
  const originalImgs = Array.from(original.querySelectorAll('img'));
  await Promise.all(
    cloneImgs.map(async (img, index) => {
      const src = originalImgs[index]?.currentSrc || originalImgs[index]?.src || img.src;
      if (!src || src.startsWith('data:')) return;
      try {
        const response = await fetch(src);
        if (!response.ok) return;
        img.src = await blobToDataUrl(await response.blob());
        img.removeAttribute('srcset');
      } catch {
        // Keep the original src if inlining fails.
      }
    }),
  );
};

// The app is a dark glass theme, which is unusable on paper. These rules force
// a plain white document with the PVS blue kept for headings and table headers.
const PRINT_STYLES = `
  @page { size: A4; margin: 14mm; }
  html, body {
    background: #ffffff !important;
    color: #111111 !important;
    margin: 0 !important;
    padding: 0 !important;
    font-size: 11pt;
  }
  *, *::before, *::after {
    background-image: none !important;
    box-shadow: none !important;
    text-shadow: none !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
    color: #111111 !important;
    background-color: transparent !important;
  }
  h1, h2, h3, h4, h5, h6, strong { color: #0437F2 !important; }
  h1 { font-size: 18pt; }
  h2 { font-size: 15pt; }
  h3 { font-size: 13pt; }
  h4, h5, h6 { font-size: 11.5pt; }
  p, li, td, th { color: #111111 !important; }
  a { color: #0437F2 !important; text-decoration: none; }
  table {
    border-collapse: collapse !important;
    border-spacing: 0 !important;
    width: 100% !important;
    border-radius: 0 !important;
    page-break-inside: auto;
  }
  th, td {
    background-color: #ffffff !important;
    border: 1px solid #666666 !important;
    padding: 5pt 7pt !important;
    text-align: left;
    vertical-align: top;
  }
  thead th {
    background-color: #e8ecff !important;
    color: #0437F2 !important;
  }
  tr, li { page-break-inside: avoid; }
  h1, h2, h3, h4 { page-break-after: avoid; }
  img { max-width: 100% !important; height: auto !important; page-break-inside: avoid; }
  hr { border-color: #999999 !important; }
  /* Layout wrappers rely on viewport sizing that makes no sense on paper. */
  [class*="min-h-screen"] { min-height: 0 !important; }
  [class*="max-w-"] { max-width: 100% !important; }
  [class*="rounded"] { border-radius: 0 !important; }
  [class*="overflow-x-auto"] { overflow: visible !important; }
  [class*="border"] { border-color: #999999 !important; }
`;

const buildPrintDocument = ({ title, css, body }) => `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
${css}
  </style>
  <style>
${PRINT_STYLES}
  </style>
</head>
<body>
${body}
</body>
</html>`;

// Hands the prepared document to the browser's print pipeline, which is what
// produces a real PDF (selectable text, proper pagination) without pulling in
// a rasterising PDF library.
const printHtmlDocument = (html) =>
  new Promise((resolve, reject) => {
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('title', 'Document archive');
    frame.style.cssText =
      'position:fixed;right:0;bottom:0;width:210mm;height:1px;opacity:0;border:0;pointer-events:none;';

    let settled = false;
    const remove = () => {
      if (frame.parentNode) frame.remove();
    };

    frame.onload = async () => {
      if (settled) return;
      settled = true;
      try {
        const win = frame.contentWindow;
        const doc = frame.contentDocument;
        const images = Array.from(doc?.images || []);
        await Promise.all(
          images
            .filter((img) => !img.complete)
            .map(
              (img) =>
                new Promise((done) => {
                  img.addEventListener('load', done, { once: true });
                  img.addEventListener('error', done, { once: true });
                  setTimeout(done, 4000);
                }),
            ),
        );
        // Give the print stylesheet a frame to apply before the dialog opens.
        await wait(150);
        win.addEventListener('afterprint', remove, { once: true });
        win.focus();
        win.print();
        // Safari never fires afterprint for iframes, so clean up on a timer too.
        setTimeout(remove, 60000);
        resolve();
      } catch (error) {
        remove();
        reject(error);
      }
    };

    frame.onerror = () => {
      remove();
      reject(new Error('Failed to prepare the archive document'));
    };

    document.body.appendChild(frame);
    frame.srcdoc = html;
  });

export const waitForArchiveReady = async (rootOrRef, { timeoutMs = 12000 } = {}) => {
  const start = Date.now();
  const getRoot = () => (rootOrRef && typeof rootOrRef === 'object' && 'current' in rootOrRef
    ? rootOrRef.current
    : rootOrRef);

  while (Date.now() - start < timeoutMs) {
    const root = getRoot();
    if (root) {
      const spinning = root.querySelector('.animate-spin');
      const images = Array.from(root.querySelectorAll('img'));
      const imagesReady = images.every((img) => img.complete);
      if (!spinning && imagesReady) {
        await wait(200);
        return root;
      }
    }
    await wait(120);
  }
  return getRoot();
};

export const archiveElementAsPdf = async (element, title) => {
  if (!element) {
    throw new Error('Nothing to archive');
  }

  const clone = element.cloneNode(true);
  clone.querySelectorAll('[data-no-archive]').forEach((node) => node.remove());
  clone.querySelectorAll('button, input, textarea, select').forEach((node) => node.remove());
  // Fixed-position chrome (the page watermark, floating buttons) would repeat
  // on every printed page.
  clone.querySelectorAll('.fixed').forEach((node) => node.remove());

  await inlineImages(clone, element);
  const css = await collectCssText();

  await printHtmlDocument(
    buildPrintDocument({ title, css, body: clone.innerHTML }),
  );
};
